import type { ReactNode } from 'react';
import { css, etiColors, mediaQueries } from '@eti/styles';

const segmentStyles = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  padding-bottom: 0;
  position: relative;

  @media ${mediaQueries.medium.up} {
    flex-direction: row;
    padding: 20px;
  }
`;

export const Segment = ({ children, ...rest }: { children: ReactNode }) => (
  <div className={segmentStyles} {...rest}>
    {children}
  </div>
);

const carrierColumnStyles = css`
  align-items: center;
  align-self: flex-start;
  display: flex;
  margin-top: 12px;

  @media ${mediaQueries.medium.up} {
    width: 18%;
  }
`;

export const CarrierColumn = ({ children }: { children: ReactNode }) => (
  <div className={carrierColumnStyles}>{children}</div>
);

const locationAndTimeWrapperStyles = css`
  flex: 1 0 23%;
  overflow: hidden;
  padding: 0 4px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${mediaQueries.large.up} {
    flex: 1 0 24%;
  }
`;

export const LocationAndTimeWrapper = ({ children }: { children: ReactNode }) => (
  <div className={locationAndTimeWrapperStyles}>{children}</div>
);

const durationWrapperStyles = css`
  align-items: center;
  display: flex;
  flex: 1 0 12%;
  flex-direction: column;
  margin-top: 12px;
  padding: 0 4px;

  @media ${mediaQueries.large.up} {
    flex: 1 0 4%;
  }
`;

export const DurationWrapper = ({ children }: { children: ReactNode }) => (
  <div className={durationWrapperStyles}>{children}</div>
);

const carrierNameStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.625rem;
  margin-right: 5px;

  [dir='rtl'] & {
    margin-left: 5px;
    margin-right: 0;
  }

  @media ${mediaQueries.medium.up} {
    font-weight: 700;
  }

  @media ${mediaQueries.large.up} {
    font-size: 0.75rem;
  }
`;

export const CarrierName = ({ children }: { children: ReactNode }) => (
  <div className={carrierNameStyles}>{children}</div>
);

const plusIconStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 1rem;
  line-height: 1rem;
  margin: 0 3px;
`;

export const PlusIcon = ({ children }: { children: ReactNode }) => (
  <span className={plusIconStyles}>{children}</span>
);

const travelModeInfoStyles = css`
  align-items: center;
  background-color: ${etiColors.grayScale[4]};
  border-radius: 10px;
  display: inline-flex;
  padding: 2px 12px;
`;

export const TravelModeInfo = ({ children, ...rest }: { children: ReactNode }) => (
  <div className={travelModeInfoStyles} {...rest}>
    {children}
  </div>
);

const travelModeTextStyles = css`
  margin-left: 5px;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 5px;
  }
`;

export const TravelModeText = ({ children, ...rest }: { children: ReactNode }) => (
  <div className={travelModeTextStyles} {...rest}>
    {children}
  </div>
);

const segmentLabelStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.625rem;
  height: 24px;
  margin-bottom: -12px;
  padding: 10px 0 0 10px;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 10px;
  }

  @media ${mediaQueries.medium.up} {
    padding-left: 20px;
    [dir='rtl'] & {
      padding-left: 0;
      padding-right: 20px;
    }
  }

  @media ${mediaQueries.large.up} {
    font-size: 0.75rem;
  }
`;

export const SegmentLabel = ({ children }: { children: ReactNode }) => (
  <div className={segmentLabelStyles}>{children}</div>
);

const tripDetailsSegmentInfoWrapperStyles = css`
  align-items: flex-start;
  display: flex;
  flex-direction: row;

  & > div:last-child {
    display: flex;

    & > div {
      margin-right: 5px;

      [dir='rtl'] & {
        margin-left: 5px;
        margin-right: 0;
      }
    }
  }

  @media ${mediaQueries.medium.up} {
    flex-direction: column;
    margin-left: 10px;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 10px;
    }

    & > div:last-child {
      display: block;

      & > div {
        margin-right: 0;

        [dir='rtl'] & {
          margin-left: 0;
        }
      }
    }
  }
`;

export const TripDetailsSegmentInfoWrapper = ({ children }: { children: ReactNode }) => (
  <div className={tripDetailsSegmentInfoWrapperStyles}>{children}</div>
);

const placeholderColumnStyles = css`
  width: 18px;
`;

export const PlaceholderColumn = () => <div className={placeholderColumnStyles} />;

export const FullWidthColumn = css`
  display: flex;
  width: 100%;
`;

export const baggageColumnWrapperClass = css`
  display: flex;
  margin-bottom: 5px;

  @media ${mediaQueries.medium.up} {
    margin-bottom: 0;
    width: 18%;
  }
`;

export const planeIconUpStyles = css`
  transform: rotate(270deg);
`;
